import { useEffect, useMemo, useState } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LineChart, Line, Legend,
} from 'recharts'
import { TicketBreakdownModal, type DetailCtx } from './ResearchTicketBreakdown'
import { ResearchExportButtons } from './ResearchExportButtons'
import { ResearchMonthCharts } from './ResearchMonthCharts'

// Research tab — POWER-ONLY analytics workbench (me.isPower).
// Chart 1: Average DAILY-SERVICE net revenue by weekday, per month of the
// running season: four mini week charts (days 1-7 / 8-14 / 15-21 / 22-end)
// plus the month aggregate, top weekday GREEN, lowest RED; then a season
// master (running average by weekday) and a progression chart showing how
// each weekday's average is moving month by month.

interface CellRow { y: number; m: number; wom: number; day_no: number; net: number; days: number }
interface Payload { season: string; from: string; to: string; rows: CellRow[] }

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const GOLD = '#c89b3c'
const GREEN = '#2e7d32'
const RED = '#b23b2e'
const DOW_COLORS = ['#0b3d2e', '#1f6e54', '#3b6d11', '#639922', '#97c459', '#c89b3c', '#8a6d1a']

const gbpK = (v: number) => (Math.abs(v) >= 1000 ? `£${(v / 1000).toFixed(1)}k` : `£${Math.round(v)}`)
const gbpFull = (v: number) => `£${Math.round(v).toLocaleString('en-GB')}`

interface DowDatum { day: string; avg: number; days: number }

/** Collapse cell rows to a Mon..Sun average-per-day series (net ÷ distinct days). */
function toDowSeries(cells: CellRow[]): DowDatum[] {
  const agg = new Map<number, { net: number; days: number }>()
  for (const c of cells) {
    const a = agg.get(c.day_no) ?? { net: 0, days: 0 }
    a.net += c.net
    a.days += c.days
    agg.set(c.day_no, a)
  }
  return DAYS.map((day, i) => {
    const a = agg.get(i + 1)
    return { day, avg: a && a.days > 0 ? a.net / a.days : 0, days: a?.days ?? 0 }
  })
}

/** Indices of the highest / lowest non-zero bars (for green/red highlighting). */
function extremes(data: DowDatum[]): { hi: number; lo: number } {
  let hi = -1, lo = -1
  data.forEach((d, i) => {
    if (d.days === 0) return
    if (hi === -1 || d.avg > data[hi].avg) hi = i
    if (lo === -1 || d.avg < data[lo].avg) lo = i
  })
  return { hi, lo }
}

function MiniDowChart({ title, cells, prevCells, height = 120, onBarClick }: { title: string; cells: CellRow[]; prevCells?: CellRow[] | null; height?: number; onBarClick?: (dayNo: number) => void }) {
  const data = useMemo(() => {
    const cur = toDowSeries(cells)
    if (!prevCells) return cur
    const prev = toDowSeries(prevCells)
    return cur.map((d, i) => ({ ...d, prev: prev[i].days > 0 ? Math.round(prev[i].avg) : null }))
  }, [cells, prevCells])
  const { hi, lo } = useMemo(() => extremes(data), [data])
  const empty = data.every((d) => d.days === 0)
  return (
    <div style={{ flex: '1 1 150px', minWidth: 150, border: '1px solid #e5e1d6', borderRadius: 8, padding: '6px 8px 2px', background: '#fff' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b6b6b', marginBottom: 2 }}>{title}</div>
      {empty ? (
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b0a89a', fontSize: '0.75rem' }}>no running days</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
            <XAxis dataKey="day" tick={{ fontSize: 8.5 }} interval={0} />
            <YAxis hide />
            <Tooltip formatter={(v, name) => [gbpFull(Number(v)), name === 'prev' ? 'last season' : 'this season']} labelFormatter={(l) => `${l} — avg/running day (click bar for tickets)`} contentStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="avg"
              radius={[2, 2, 0, 0]}
              cursor={onBarClick ? 'pointer' : undefined}
              onClick={(d: { day?: string }) => { if (onBarClick && d?.day) onBarClick(DAYS.indexOf(d.day) + 1) }}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={i === hi ? GREEN : i === lo ? RED : GOLD} />
              ))}
            </Bar>
            {prevCells && (
              <Bar dataKey="prev" fill="rgba(0,0,0,0)" stroke="#8a6d1a" strokeWidth={1.5} strokeDasharray="4 2" radius={[2, 2, 0, 0]} />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

export function ResearchDashboard({ me }: { me: { isPower: boolean; upn?: string } }) {
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<DetailCtx | null>(null)
  // Last-year wireframe overlay on the month-by-month weekday charts.
  const [showLY, setShowLY] = useState(false)
  const [prevData, setPrevData] = useState<Payload | null>(null)
  const [prevErr, setPrevErr] = useState<string | null>(null)
  const prevSeason = data ? data.season.split('/').map((n) => String(Number(n) - 1)).join('/') : null

  useEffect(() => {
    if (!showLY || !prevSeason || prevData) return
    let live = true
    fetch(`/api/research/weekday-revenue?season=${encodeURIComponent(prevSeason)}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error((b as { error?: string }).error || `${r.status}`) } return r.json() as Promise<Payload> })
      .then((d) => { if (live) { setPrevData(d); setPrevErr(null) } })
      .catch((e) => { if (live) setPrevErr(String((e as Error)?.message ?? e)) })
    return () => { live = false }
  }, [showLY, prevSeason, prevData])

  useEffect(() => {
    if (!me.isPower) { setLoading(false); return }
    let live = true
    fetch('/api/research/weekday-revenue')
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error((b as { error?: string }).error || `${r.status}`) } return r.json() as Promise<Payload> })
      .then((d) => { if (live) { setData(d); setError(null) } })
      .catch((e) => { if (live) setError(String((e as Error)?.message ?? e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [me.isPower])

  // Months present (season order), each with its cells.
  const months = useMemo(() => {
    const out: { key: string; label: string; cells: CellRow[] }[] = []
    const byKey = new Map<string, CellRow[]>()
    for (const r of data?.rows ?? []) {
      const key = `${r.y}-${String(r.m).padStart(2, '0')}`
      const arr = byKey.get(key) ?? []
      arr.push(r)
      byKey.set(key, arr)
    }
    for (const key of [...byKey.keys()].sort()) {
      const [y, m] = key.split('-').map(Number)
      out.push({ key, label: `${MONTHS[m - 1]} ${y}`, cells: byKey.get(key)! })
    }
    return out
  }, [data])

  // Master: season-to-date by weekday.
  const master = useMemo(() => toDowSeries(data?.rows ?? []), [data])
  const masterEx = useMemo(() => extremes(master), [master])

  // Progression: each weekday's avg-per-running-day, per month.
  const progression = useMemo(() => {
    return months.map((mo) => {
      const series = toDowSeries(mo.cells)
      const point: Record<string, number | string | null> = { month: mo.label }
      series.forEach((d) => { point[d.day] = d.days > 0 ? Math.round(d.avg) : null })
      return point
    })
  }, [months])

  if (!me.isPower) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Research — restricted</strong>
          <p>This workbench is limited to <code>SG-Reports-Power</code> (and admins). Ask Chris Flynn for access.</p>
        </div>
      </main>
    )
  }

  return (
    <main className="app-main">
      <div className="budget-toolbar">
        <div>
          <p className="budget-sub">
            Analytics workbench — daily-service revenue structure{data ? ` · season ${data.season}, ${data.from} → ${data.to} (to date)` : ''}.
            Power users only. <strong>This page is a work in progress and any outcomes or suggestions must be validated by your own
            external means. You should not base strategic economic decisions on these conclusions alone.</strong>
          </p>
        </div>
      </div>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="placeholder" style={{ borderColor: '#a33' }}>
          <strong>Couldn&apos;t load research data</strong>
          <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>
        </div>
      )}

      {data && !loading && !error && months.length === 0 && (
        <div className="placeholder"><strong>No daily-service sales in this season yet.</strong></div>
      )}

      {months.length > 0 && (
        <>
          <section className="sales-section">
            <div className="sales-section-head">
              <h2>Average daily-service revenue by weekday — month by month</h2>
              <span className="sales-section-sub">
                Average net £ per <em>running</em> day. Weeks are days 1–7 / 8–14 / 15–21 / 22–end of month.
                <span style={{ color: GREEN, fontWeight: 600 }}> Green</span> = best weekday,
                <span style={{ color: RED, fontWeight: 600 }}> red</span> = weakest.
                Click any bar for the ticket breakdown.
                <br />
                <em>Basis: travel revenue only — admissions and train-session sales. Gift cards, vouchers, retail and
                catering rung through under a daily booking type are excluded (they were previously showing as
                &quot;service revenue&quot; on non-running days in Feb/Mar).</em>
              </span>
            </div>
            <div style={{ margin: '2px 0 8px' }}>
              <button
                type="button"
                onClick={() => setShowLY((v) => !v)}
                style={{
                  background: showLY ? '#0b3d2e' : '#fff', color: showLY ? '#fff' : '#0b3d2e',
                  border: '1px solid #0b3d2e', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                }}
              >{showLY ? 'Hide' : 'Show'} last year&apos;s revenue{prevSeason ? ` (${prevSeason})` : ''}</button>
              {showLY && !prevData && !prevErr && <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#6b6b6b' }}>loading last season…</span>}
              {showLY && prevErr && <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#a33' }}>{prevErr}</span>}
              {showLY && prevData && <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#8a6d1a' }}>dashed outline = {prevSeason} avg per running day</span>}
            </div>
            {months.map((mo) => {
              const [my, mm] = mo.key.split('-').map(Number)
              const prevFor = (w: number | null): CellRow[] | null =>
                showLY && prevData ? prevData.rows.filter((c) => c.y === my - 1 && c.m === mm && (w == null || c.wom === w)) : null
              return (
                <div key={mo.key} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', margin: '10px 0 6px', color: '#0b3d2e' }}>{mo.label}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4].map((w) => (
                      <MiniDowChart
                        key={w}
                        title={`Week ${w}`}
                        cells={mo.cells.filter((c) => c.wom === w)}
                        prevCells={prevFor(w)}
                        onBarClick={(dayNo) => {
                          setDetail({ dayNo, y: my, m: mm, wom: w, label: `${DAYS[dayNo - 1]}s, ${mo.label} week ${w}` })
                        }}
                      />
                    ))}
                    <div style={{ flex: '1.4 1 210px', minWidth: 210, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <MiniDowChart
                        title={`${mo.label} — whole month`}
                        cells={mo.cells}
                        prevCells={prevFor(null)}
                        height={120}
                        onBarClick={(dayNo) => {
                          setDetail({ dayNo, y: my, m: mm, wom: null, label: `${DAYS[dayNo - 1]}s, ${mo.label}` })
                        }}
                      />
                      {/* Download the whole month: every week as its own tab / page,
                          the month summation, and the top-ten services. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.68rem', color: '#8a8a8a', fontWeight: 600 }}>Download {mo.label}:</span>
                        <ResearchExportButtons scope="month" month={mm} monthLabel={mo.label} />
                      </div>
                    </div>
                  </div>
                  {/* Revenue / capacity / load charts for the month. Collapsed by
                      default — each one fetches the month's full service data. */}
                  <ResearchMonthCharts month={mm} monthLabel={mo.label} />
                </div>
              )
            })}
          </section>

          <section className="sales-section">
            <div className="sales-section-head">
              <h2>Season to date — running average by weekday</h2>
              <span className="sales-section-sub">All months so far combined; average net £ per running day.</span>
            </div>
            {/* Season download: a tab / page per month, the season-to-date
                summation, and top-ten services and ticket types. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 8px' }}>
              <span style={{ fontSize: '0.72rem', color: '#8a8a8a', fontWeight: 600 }}>Download season to date:</span>
              <ResearchExportButtons scope="season" />
            </div>
            <div style={{ border: '1px solid #e5e1d6', borderRadius: 8, padding: '10px 8px 4px', background: '#fff' }}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={master} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => gbpK(Number(v))} tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => gbpFull(Number(v))} labelFormatter={(l) => `${l} — season avg/running day (click bar for tickets)`} />
                  <Bar
                    dataKey="avg"
                    radius={[3, 3, 0, 0]}
                    cursor="pointer"
                    onClick={(d: { day?: string }) => {
                      if (d?.day) {
                        const dayNo = DAYS.indexOf(d.day) + 1
                        setDetail({ dayNo, y: null, m: null, wom: null, label: `${DAYS[dayNo - 1]}s, whole season${data ? ` ${data.season}` : ''}` })
                      }
                    }}
                  >
                    {master.map((_, i) => (
                      <Cell key={i} fill={i === masterEx.hi ? GREEN : i === masterEx.lo ? RED : GOLD} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="sales-section">
            <div className="sales-section-head">
              <h2>How the shape is changing as the season progresses</h2>
              <span className="sales-section-sub">Each line is one weekday&apos;s average £ per running day, month by month — watch lines converge, diverge or swap places.</span>
            </div>
            <div style={{ border: '1px solid #e5e1d6', borderRadius: 8, padding: '10px 8px 4px', background: '#fff' }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={progression} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => gbpK(Number(v))} tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => gbpFull(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {DAYS.map((d, i) => (
                    <Line key={d} type="monotone" dataKey={d} stroke={DOW_COLORS[i]} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}

      {detail && <TicketBreakdownModal ctx={detail} onClose={() => setDetail(null)} />}
    </main>
  )
}
